import { useTranslation } from "@bounce/i18n"

import type { Interpolations } from "./useInterpolations"
import type { CityData } from "@/libs/bounce/cities"
import { interpolateText } from "@/libs/prismic/helpers"

export type UseSEOOptions = {
  city: Pick<CityData, "splitTestData" | "storeCount" | "metaDescription" | "pois" | "slug">
  interpolations: Interpolations
}

const MAX_POIS_DESCRIPTION = 3

const useMetaTitle = ({ city, interpolations }: UseSEOOptions) => {
  const { t } = useTranslation("marketing-ls-city")

  if (city.splitTestData.metaTitle) {
    return interpolateText(city.splitTestData.metaTitle, interpolations)
  }

  if (!city.storeCount || city.storeCount < 5) {
    return t(
      "page.city.metaTitleLessLocationsV8",
      "Luggage Storage {{CITY_NAME}} From {{PRICE}}/Day | Bounce",
      interpolations,
    )
  }

  return t(
    "page.city.metaTitleV20",
    "{CITY_NAME} Luggage Storage & Lockers From {PRICE}/Day | {count, plural, one {# Spot} other {# Spots}}",
    {
      ...interpolations,
      count: Number(interpolations.STORAGE_SPOT_COUNT),
    },
  )
}

const useMetaDescription = ({ city, interpolations }: UseSEOOptions) => {
  const { t } = useTranslation("marketing-ls-city")

  const pois = city.pois?.map(({ name }) => name) ?? []
  if (pois.length === 0) {
    return t(
      "page.city.metaDescriptionSYB10.noPois",
      "Choose from {{STORAGE_SPOT_COUNT}} luggage storage spots & lockers in {{CITY_NAME}}. Find 15,000+ Bounce locations in 4,000+ cities worldwide.",
      interpolations,
    )
  }

  return t(
    "page.city.metaDescriptionSYB10.withPois",
    "Choose from {{STORAGE_SPOT_COUNT}} luggage storage spots & lockers in {{CITY_NAME}}, near {{POI_STRING}} and beyond. Find Bounce in 4,000+ cities worldwide.",
    {
      ...interpolations,
      POI_STRING: pois.slice(0, MAX_POIS_DESCRIPTION).join(", "),
    },
  )
}

export const useSEO = ({ city, interpolations }: UseSEOOptions) => {
  const metaTitle = useMetaTitle({ city, interpolations })
  const metaDescription = useMetaDescription({ city, interpolations })

  return {
    metaTitle,
    metaDescription,
  }
}
